import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import EmberTicker from '@/pages/home/components/EmberTicker';

const HEADSHOT = 'https://storage.readdy-site.link/project_files/436b8b39-7177-4764-8ecd-b07483d3e043/a5d35949-8502-4d33-83d0-e2a2557868e9_Headshot_winter.png?v=f99ebff7eac177a40cc739df5778eb7e';
const BTN_BLUE_GREY = '#5b8ab5';

const videoGridItems = [
  {
    // Slot 1 — soccer (compressed)
    type: 'video',
    src: 'https://storage.readdy-site.link/project_files/436b8b39-7177-4764-8ecd-b07483d3e043/1963f706-eaf9-4b9b-894f-2bd71cc49100_soccer-Anna-Schriver.mp4?v=c6c2c14189e9523f9079e6fb826184b2',
    poster: 'https://public.readdy.ai/ai/img_res/0f89e6df4bbc3267513cc8d248c41557.jpg',
  },
  {
    // Slot 2 — blanchet (updated v2 compressed, web optimized)
    type: 'video',
    src: 'https://storage.readdy-site.link/project_files/436b8b39-7177-4764-8ecd-b07483d3e043/54f3dba5-6545-4780-ac9e-4da4cf3ac3ec_blanchet-Anna-Schriver_updated33_compressed_web-optimized.mp4?v=3e1fdfab8ca0836efe8532543ed223d4',
    poster: 'https://readdy.ai/api/search-image?query=elegant%20woman%20in%20stylish%20outfit%20cinematic%20portrait%20dark%20moody%20studio%20lighting%20dramatic%20shadows%20black%20background%20fashion%20editorial%20film%20grain%20high%20contrast&width=600&height=800&seq=poster-blanchet-2&orientation=portrait',
  },
  {
    // Slot 3 — handstand (compressed)
    type: 'video',
    src: 'https://storage.readdy-site.link/project_files/436b8b39-7177-4764-8ecd-b07483d3e043/670cab69-1d27-4e25-b790-1c3f12f3db1e_handstand-Anna-Schriver.mp4?v=01f90305deb6033459d32bcb6de0e785',
    poster: 'https://readdy.ai/api/search-image?query=athletic%20woman%20doing%20handstand%20gymnastics%20acrobatics%20cinematic%20dark%20moody%20lighting%20dramatic%20black%20background%20minimal%20clean%20portrait%20style%20film%20grain&width=600&height=800&seq=poster-handstand-3&orientation=portrait',
  },
  {
    // Slot 4 — vb (compressed)
    type: 'video',
    src: 'https://storage.readdy-site.link/project_files/436b8b39-7177-4764-8ecd-b07483d3e043/730bc655-9204-446c-a654-1f4bb470f705_vb-Anna-Schriver_updated.mp4?v=ce801226e6e369eeac5725bd18179b17',
    poster: 'https://readdy.ai/api/search-image?query=volleyball%20player%20woman%20athlete%20action%20shot%20cinematic%20dark%20moody%20dramatic%20lighting%20black%20background%20minimal%20clean%20portrait%20style%20film%20grain%20high%20contrast&width=600&height=800&seq=poster-vb-4&orientation=portrait',
  },
  {
    // Slot 5 — solo_shot (compressed)
    type: 'video',
    src: 'https://storage.readdy-site.link/project_files/436b8b39-7177-4764-8ecd-b07483d3e043/8c50f1a1-180a-45f9-a600-7063e359c82e_solo_shot-Anna-Schriver.mp4?v=445521b1cfdeff83c74f8a7950ca252f',
    poster: 'https://readdy.ai/api/search-image?query=young%20woman%20solo%20portrait%20cinematic%20dark%20moody%20dramatic%20lighting%20black%20background%20minimal%20clean%20film%20grain%20high%20contrast%20editorial%20photography&width=600&height=800&seq=poster-solo-5&orientation=portrait',
  },
  {
    // Slot 6 — piphi (compressed)
    type: 'video',
    src: 'https://storage.readdy-site.link/project_files/436b8b39-7177-4764-8ecd-b07483d3e043/e82de1e9-b23e-46e9-ab04-389932f517ab_piphi-Anna-Schriver.mp4?v=8a8a9b61c3548f6fcc14527ac9eca9fa',
    poster: 'https://readdy.ai/api/search-image?query=college%20women%20group%20sorority%20lifestyle%20cinematic%20dark%20moody%20dramatic%20lighting%20black%20background%20minimal%20clean%20portrait%20style%20film%20grain%20high%20contrast&width=600&height=800&seq=poster-piphi-6&orientation=portrait',
  },
];

function VideoGridCard({ item, brightnessBoost }: { item: typeof videoGridItems[number]; brightnessBoost?: number }) {
  const brightness = brightnessBoost ?? 0.85;
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) return;

    // Mark as loaded once the first frame is available
    const handleLoadedData = () => setLoaded(true);
    video.addEventListener('loadeddata', handleLoadedData);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { rootMargin: '25% 0px', threshold: 0.05 }
    );

    observer.observe(container);
    return () => {
      observer.disconnect();
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, []);

  const handleClick = () => {
    navigate('/projects');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative aspect-[3/4] overflow-hidden rounded-lg group cursor-pointer"
      style={{ transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
    >
      {/* Shimmer skeleton — visible until first frame loads */}
      <div
        className="absolute inset-0 z-10 pointer-events-none transition-opacity duration-700"
        style={{ opacity: loaded ? 0 : 1 }}
        aria-hidden="true"
      >
        <div className="w-full h-full shimmer-card" />
      </div>

      <video
        ref={videoRef}
        loop
        muted
        playsInline
        preload="metadata"
        src={item.src}
        poster={item.poster}
        className="w-full h-full object-cover"
        style={{
          filter: `brightness(${hovered ? Math.min(brightness + 0.22, 1.5) : brightness})`,
          transform: hovered ? 'scale(1.07)' : 'scale(1)',
          transition: 'transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94), filter 0.5s ease',
          willChange: 'transform',
        }}
      />

      {/* Jamison-style overlay: single clean gradient, dark bottom fade */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-500 group-hover:opacity-60"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.38) 40%, rgba(0,0,0,0.10) 70%, transparent 100%)' }}
      />

      {/* Subtle top darkening to frame the card */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-500 group-hover:opacity-50"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, transparent 30%)' }}
      />

      {/* Hover brightness glow */}
      <div
        className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 50%, rgba(255,255,255,0.07) 0%, transparent 70%)' }}
      />
    </div>
  );
}

// All letters across both words, with their word membership
const ALL_LETTERS = [
  { char: 'A', word: 'anna', idx: 0 },
  { char: 'N', word: 'anna', idx: 1 },
  { char: 'N', word: 'anna', idx: 2 },
  { char: 'A', word: 'anna', idx: 3 },
  { char: 'S', word: 'schriver', idx: 4 },
  { char: 'C', word: 'schriver', idx: 5 },
  { char: 'H', word: 'schriver', idx: 6 },
  { char: 'R', word: 'schriver', idx: 7 },
  { char: 'I', word: 'schriver', idx: 8 },
  { char: 'V', word: 'schriver', idx: 9 },
  { char: 'E', word: 'schriver', idx: 10 },
  { char: 'R', word: 'schriver', idx: 11 },
];

const TITLE_COLOR = '#f9feff';
const BLUE_GREY = '#7aa3c4';

// Pick a random letter index that is NOT adjacent to any currently dimmed letter
function pickNonAdjacent(dimmed: Set<number>): number | null {
  const candidates = ALL_LETTERS
    .map((_, i) => i)
    .filter(i => {
      if (dimmed.has(i)) return false;
      if (dimmed.has(i - 1)) return false;
      if (dimmed.has(i + 1)) return false;
      return true;
    });
  if (candidates.length === 0) return null;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

// Count how many letters in ANNA (indices 0-3) are currently dimmed
function annaCount(dimmed: Set<number>): number {
  return [0, 1, 2, 3].filter(i => dimmed.has(i)).length;
}

export default function HeroSection() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Track which letter indices are currently "dimmed" to blue-grey
  const [dimmed, setDimmed] = useState<Set<number>>(new Set());
  const dimmedRef = useRef<Set<number>>(new Set());

  // One-time RGB glitch flicker on page load
  const [titleGlitch, setTitleGlitch] = useState(false);
  const glitchFiredRef = useRef(false);

  useEffect(() => {
    // Each letter gets its own independent timer that fires after a random startup delay
    // so nothing happens simultaneously on load
    const timers: ReturnType<typeof setTimeout>[] = [];

    ALL_LETTERS.forEach((_, i) => {
      // Longer startup delay: 4s–24s — more spread out, less busy on load
      const startDelay = 4000 + Math.random() * 20000;
      // Longer cycle: 14s–26s — much less frequent pulses
      const cycleDuration = 14000 + Math.random() * 12000;
      // Dim duration: 3s–6s — stays blue a bit longer when it does fire
      const dimDuration = 3000 + Math.random() * 3000;

      const scheduleCycle = (delay: number) => {
        const t = setTimeout(() => {
          const current = dimmedRef.current;
          const isAdjacentDimmed = current.has(i - 1) || current.has(i + 1) || current.has(i);
          // Max 2 letters total; heavily bias toward 1 — only allow 2nd if random chance passes
          const tooMany = current.size >= 2;
          // Extra throttle: even if under limit, 60% chance to skip this cycle (keeps it sparse)
          const skipChance = current.size >= 1 && Math.random() < 0.60;
          const isAnna = i <= 3;
          const annaAlreadyDimmed = isAnna && annaCount(current) >= 1;
          if (!isAdjacentDimmed && !tooMany && !skipChance && !annaAlreadyDimmed) {
            const next = new Set(current);
            next.add(i);
            dimmedRef.current = next;
            setDimmed(new Set(next));

            const restore = setTimeout(() => {
              const restored = new Set(dimmedRef.current);
              restored.delete(i);
              dimmedRef.current = restored;
              setDimmed(new Set(restored));
            }, dimDuration);
            timers.push(restore);
          }
          // Longer random jitter on reschedule too
          scheduleCycle(cycleDuration + Math.random() * 8000);
        }, delay);
        timers.push(t);
      };

      scheduleCycle(startDelay);
    });

    return () => timers.forEach(clearTimeout);
  }, []);

  // One-time RGB glitch on load — fires immediately on mount, never again
  useEffect(() => {
    if (glitchFiredRef.current) return;
    glitchFiredRef.current = true;
    setTitleGlitch(true);
    // Glitch lasts ~600ms then clears
    const t = setTimeout(() => setTitleGlitch(false), 600);
    return () => clearTimeout(t);
  }, []);

  const getLetterStyle = (globalIdx: number, isSchriver: boolean, charIdx: number) => ({
    display: 'inline-block' as const,
    color: dimmed.has(globalIdx) ? BLUE_GREY : TITLE_COLOR,
    transition: 'color 4.5s cubic-bezier(0.25, 0, 0.1, 1)',
    ...(isSchriver && charIdx === 3 ? { marginLeft: '-5px' } : {}),
  });

  const handleSeeAllProjects = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/projects');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  const handleNavProjects = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/projects');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  const handleNavAbout = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/about');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  const handleNavContact = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/contact');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  return (
    <section className="relative overflow-x-hidden" style={{ background: '#000000' }}>

      {/* ── ANIMATED SMOKE BACKGROUND ── */}
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        <div
          className="absolute animate-glow-pulse"
          style={{
            top: '-10%', left: '30%',
            width: '70vw', height: '70vw',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,90,0,0.13) 0%, transparent 65%)',
            filter: 'blur(60px)',
          }}
        />
        <div
          className="absolute animate-glow-pulse-2"
          style={{
            bottom: '5%', right: '-10%',
            width: '55vw', height: '55vw',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(200,60,0,0.09) 0%, transparent 65%)',
            filter: 'blur(80px)',
          }}
        />
        <div
          className="absolute animate-smoke-1"
          style={{
            top: '5%', left: '-5%',
            width: '65vw', height: '55vh',
            background: 'radial-gradient(ellipse 100% 80% at 30% 40%, rgba(210,180,150,0.14) 0%, transparent 65%)',
            filter: 'blur(40px)',
            mixBlendMode: 'screen',
          }}
        />
        <div
          className="absolute animate-smoke-2"
          style={{
            top: '20%', right: '-8%',
            width: '55vw', height: '60vh',
            background: 'radial-gradient(ellipse 90% 70% at 70% 50%, rgba(190,160,130,0.11) 0%, transparent 60%)',
            filter: 'blur(50px)',
            mixBlendMode: 'screen',
          }}
        />
        <div
          className="absolute animate-smoke-3"
          style={{
            bottom: '-5%', left: '20%',
            width: '60vw', height: '50vh',
            background: 'radial-gradient(ellipse 110% 80% at 50% 80%, rgba(170,140,110,0.12) 0%, transparent 60%)',
            filter: 'blur(45px)',
            mixBlendMode: 'screen',
          }}
        />
        {[
          { left: '12%', top: '70%', size: 2, cls: 'animate-particle-1', color: 'rgba(255,140,60,0.7)' },
          { left: '28%', top: '80%', size: 1.5, cls: 'animate-particle-2', color: 'rgba(255,160,80,0.5)' },
          { left: '45%', top: '75%', size: 2.5, cls: 'animate-particle-3', color: 'rgba(255,120,40,0.6)' },
          { left: '60%', top: '82%', size: 1.5, cls: 'animate-particle-4', color: 'rgba(220,180,140,0.5)' },
          { left: '72%', top: '72%', size: 2, cls: 'animate-particle-5', color: 'rgba(255,140,60,0.65)' },
          { left: '85%', top: '78%', size: 1.5, cls: 'animate-particle-6', color: 'rgba(255,100,30,0.55)' },
        ].map((p, i) => (
          <div
            key={i}
            className={`absolute rounded-full ${p.cls}`}
            style={{
              left: p.left,
              top: p.top,
              width: `${p.size * 3}px`,
              height: `${p.size * 3}px`,
              background: p.color,
              filter: 'blur(1px)',
              boxShadow: `0 0 ${p.size * 4}px ${p.color}`,
            }}
          />
        ))}
      </div>

      {/* TOP TICKER BAR — Ember Gradient */}
      <EmberTicker />

      {/* MAIN NAV ROW */}
      <nav className="relative z-50 border-b border-white/10" style={{ background: '#000000', marginTop: '20px' }}>
        <div className="px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="cursor-pointer flex items-center">
            <img
              src="https://storage.readdy-site.link/project_files/436b8b39-7177-4764-8ecd-b07483d3e043/03ebce5f-d5da-4799-81c4-50550e752dac_ANNA-LOGO_INITIALS-WHITE.png?v=2a513169e4cb9aa0ba1be00f988dd533"
              alt="Anna Schriver"
              className="h-[57px] w-auto object-contain"
            />
          </Link>
          <div className="hidden md:flex items-center gap-10">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); window.scrollTo({ top: 0, behavior: 'instant' }); }} className="text-white/70 text-sm font-semibold tracking-widest uppercase hover:text-white transition-colors whitespace-nowrap cursor-pointer">Welcome</a>
            <a href="/projects" onClick={handleNavProjects} className="text-white/70 text-sm font-semibold tracking-widest uppercase hover:text-white transition-colors whitespace-nowrap cursor-pointer">Projects</a>
            <a href="/about" onClick={handleNavAbout} className="text-white/70 text-sm font-semibold tracking-widest uppercase hover:text-white transition-colors whitespace-nowrap cursor-pointer">About Me</a>
            <a
              href="/contact"
              onClick={handleNavContact}
              className="text-white px-6 py-2 text-sm font-bold tracking-widest uppercase whitespace-nowrap cursor-pointer rounded-md btn-glow"
              style={{ background: BTN_BLUE_GREY }}
            >
              GET IN TOUCH
            </a>
          </div>
          <button className="md:hidden text-white cursor-pointer" onClick={() => setMenuOpen(!menuOpen)}>
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
        <div className="h-px bg-white/10 w-full"></div>
        {menuOpen && (
          <div className="md:hidden px-4 py-6 flex flex-col gap-4" style={{ background: '#000000' }}>
            <a href="/" className="text-white text-sm font-semibold tracking-widest uppercase" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/'); window.scrollTo({ top: 0, behavior: 'instant' }); }}>Welcome</a>
            <a href="/projects" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/projects'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50); }} className="text-white text-sm font-semibold tracking-widest uppercase">Projects</a>
            <a href="/about" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/about'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50); }} className="text-white text-sm font-semibold tracking-widest uppercase">About Me</a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/contact'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50); }} className="text-white text-sm font-semibold tracking-widest uppercase">Get in Touch</a>
          </div>
        )}
      </nav>

      {/* ── ANNA SCHRIVER TITLE — overlaps into grid ── */}
      <div className="relative z-30 pt-10 md:pt-[66px] px-4 md:px-8 pb-0 hidden md:block" id="hero">
        <div className="w-full relative">
          {/* Base title */}
          <div className="flex flex-wrap items-baseline justify-center" style={{ gap: '0' }}>
            {/* ANNA — bold weight */}
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 900,
                fontSize: 'clamp(2.6rem, 11vw, 11rem)',
                lineHeight: 1,
                letterSpacing: '-0.02em',
              }}
            >
              {['A','N','N','A'].map((char, i) => (
                <span key={i} style={getLetterStyle(i, false, i)}>
                  {char}
                </span>
              ))}
            </span>
            {/* SCHRIVER — thin weight, on its own line on mobile */}
            <span
              className="block md:inline"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 100,
                fontSize: 'clamp(2.6rem, 11vw, 11rem)',
                lineHeight: 1,
                letterSpacing: '-0.08em',
                marginLeft: '-0.03em',
              }}
            >
              {['S','C','H','R','I','V','E','R'].map((char, i) => (
                <span key={i} style={getLetterStyle(i + 4, true, i)}>
                  {char}
                </span>
              ))}
            </span>
          </div>

          {/* RGB glitch layers — only visible during one-time flicker */}
          {titleGlitch && (
            <>
              {/* Red channel — shifts left */}
              <div
                className="absolute inset-0 flex flex-wrap items-baseline justify-center pointer-events-none title-glitch-red"
                style={{ gap: '0' }}
                aria-hidden="true"
              >
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 900, fontSize: 'clamp(2.6rem, 11vw, 11rem)', lineHeight: 1, letterSpacing: '-0.02em', color: 'rgba(255,30,30,0.75)' }}>ANNA</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 100, fontSize: 'clamp(2.6rem, 11vw, 11rem)', lineHeight: 1, letterSpacing: '-0.04em', color: 'rgba(255,30,30,0.75)' }}>SCHRIVER</span>
              </div>
              {/* Cyan channel — shifts right */}
              <div
                className="absolute inset-0 flex flex-wrap items-baseline justify-center pointer-events-none title-glitch-cyan"
                style={{ gap: '0' }}
                aria-hidden="true"
              >
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 900, fontSize: 'clamp(2.6rem, 11vw, 11rem)', lineHeight: 1, letterSpacing: '-0.02em', color: 'rgba(0,220,255,0.70)' }}>ANNA</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 100, fontSize: 'clamp(2.6rem, 11vw, 11rem)', lineHeight: 1, letterSpacing: '-0.04em', color: 'rgba(0,220,255,0.70)' }}>SCHRIVER</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── VIDEO GRID — hidden on mobile, shown on md+ ── */}
      <div className="relative z-10 px-4 pb-20 hidden md:block" style={{ marginTop: '-4rem' }}>

        {/* === FULL-GRID DARK BLANKET — this is the Jamison effect === */}
        {/* Sits above all cards, dims the entire grid as one unit */}
        <div
          className="absolute inset-0 pointer-events-none z-20"
          style={{ background: 'rgba(0,0,0,0.35)' }}
        />

        {/* Ultra-feathered black bleed from top — very long, very soft */}
        <div
          className="absolute top-0 left-0 right-0 pointer-events-none z-30"
          style={{
            height: '620px',
            background: 'linear-gradient(to bottom, #000000 0%, #000000 10%, rgba(0,0,0,0.85) 18%, rgba(0,0,0,0.65) 28%, rgba(0,0,0,0.42) 40%, rgba(0,0,0,0.22) 54%, rgba(0,0,0,0.10) 68%, rgba(0,0,0,0.03) 82%, transparent 100%)',
          }}
        />

        {/* Hard black bleed from bottom — merges into section below */}
        <div
          className="absolute bottom-0 left-0 right-0 pointer-events-none z-30"
          style={{
            height: '180px',
            background: 'linear-gradient(to top, #000000 0%, rgba(0,0,0,0.85) 50%, transparent 100%)',
          }}
        />

        <div className="max-w-7xl mx-auto grid grid-cols-3 gap-4">
          <div className="space-y-4">
            <VideoGridCard item={videoGridItems[0]} brightnessBoost={1.20} />
            <VideoGridCard item={videoGridItems[1]} brightnessBoost={1.08} />
          </div>
          <div className="space-y-4 mt-16">
            <VideoGridCard item={videoGridItems[2]} brightnessBoost={0.90} />
            <VideoGridCard item={videoGridItems[3]} brightnessBoost={0.90} />
          </div>
          <div className="space-y-4">
            <VideoGridCard item={videoGridItems[4]} brightnessBoost={1.08} />
            <VideoGridCard item={videoGridItems[5]} brightnessBoost={0.90} />
          </div>
        </div>
      </div>

      {/* ── MOBILE IDENTITY + VIDEO GRID — shown only on mobile ── */}
      <div id="hero" className="relative z-10 md:hidden" style={{ marginTop: '40px' }}>

        {/* Stylized name above the grid */}
        <div className="px-4 pb-2 text-center">
          <div className="flex items-baseline justify-center">
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 900,
                fontSize: 'clamp(2.4rem, 10.5vw, 3.8rem)',
                lineHeight: 1,
                letterSpacing: '-0.02em',
                color: TITLE_COLOR,
              }}
            >
              ANNA
            </span>
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 100,
                fontSize: 'clamp(2.4rem, 10.5vw, 3.8rem)',
                lineHeight: 1,
                letterSpacing: '-0.08em',
                color: TITLE_COLOR,
                marginLeft: '0.04em',
              }}
            >
              SCHRIVER
            </span>
          </div>
        </div>

        {/* Grid wrapper — 4 compressed videos */}
        <div className="relative px-4 pb-10">
          {/* Dark blanket */}
          <div className="absolute inset-0 pointer-events-none z-20" style={{ background: 'rgba(0,0,0,0.35)' }} />
          {/* Top fade — ultra-feathered, barely perceptible, very long and smooth */}
          <div className="absolute top-0 left-0 right-0 pointer-events-none z-30" style={{ height: '260px', background: 'linear-gradient(to bottom, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.52) 8%, rgba(0,0,0,0.34) 18%, rgba(0,0,0,0.20) 30%, rgba(0,0,0,0.10) 45%, rgba(0,0,0,0.04) 62%, rgba(0,0,0,0.01) 78%, transparent 100%)' }} />
          {/* Bottom fade */}
          <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-30" style={{ height: '100px', background: 'linear-gradient(to top, #000000 0%, rgba(0,0,0,0.7) 50%, transparent 100%)' }} />
          <div className="grid grid-cols-2 gap-3">
            {/* Top-left: solo shot global */}
            <VideoGridCard item={videoGridItems[4]} brightnessBoost={1.05} />
            {/* Top-right: handstand */}
            <VideoGridCard item={videoGridItems[2]} brightnessBoost={0.95} />
            {/* Bottom-left: piphi */}
            <VideoGridCard item={videoGridItems[5]} brightnessBoost={0.90} />
            {/* Bottom-right: vb */}
            <VideoGridCard item={videoGridItems[3]} brightnessBoost={0.90} />
          </div>
        </div>
      </div>

      {/* ── SCROLL DOWN INDICATOR — desktop only ── */}
      <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-40 flex-col items-center gap-2 pointer-events-none">
        <span className="text-white/30 text-[10px] font-semibold tracking-[0.25em] uppercase">Scroll</span>
        <div className="scroll-chevron-wrap flex flex-col items-center gap-[3px]">
          <div className="scroll-chevron" style={{ animationDelay: '0ms' }} />
          <div className="scroll-chevron" style={{ animationDelay: '160ms' }} />
          <div className="scroll-chevron" style={{ animationDelay: '320ms' }} />
        </div>
      </div>

      {/* About Section */}
      <div className="relative z-10 py-16 md:py-24 px-4 md:px-8" id="about" style={{ background: '#0d0d0f' }}>
        <div className="absolute inset-0 pointer-events-none">
        </div>
        <div className="max-w-5xl mx-auto">
          {/* Centered Anna + headshot block */}
          <div className="flex flex-col items-center text-center gap-6 mb-14">
            {/* Headshot with pulsing inner glow */}
            <div className="relative flex-shrink-0 w-28 h-28">
              {/* Outer ambient glow — sits behind the photo */}
              <div
                className="absolute animate-headshot-glow pointer-events-none"
                style={{
                  inset: '-10px',
                  background: 'radial-gradient(ellipse at center, rgba(91,138,181,0.45) 0%, rgba(91,138,181,0.15) 45%, transparent 70%)',
                  filter: 'blur(10px)',
                  borderRadius: 0,
                }}
              />
              <img
                src={HEADSHOT}
                alt="Anna Schriver"
                className="relative w-28 h-28 object-cover object-top z-10"
                style={{ borderRadius: 0 }}
              />
              {/* Inner glow overlay — bleeds inward from edges */}
              <div
                className="absolute inset-0 pointer-events-none z-20 animate-headshot-glow"
                style={{
                  background: 'radial-gradient(ellipse at center, transparent 40%, rgba(91,138,181,0.18) 70%, rgba(91,138,181,0.35) 100%)',
                  borderRadius: 0,
                }}
              />
              {/* Color-tinted edge burn — warm orange top, cool blue bottom */}
              <div
                className="absolute inset-0 pointer-events-none z-30"
                style={{
                  background: `
                    radial-gradient(ellipse 120% 60% at 50% 0%,   rgba(255,110,30,0.38) 0%, transparent 65%),
                    radial-gradient(ellipse 120% 60% at 50% 100%, rgba(30,80,160,0.32)  0%, transparent 65%),
                    radial-gradient(ellipse 60% 120% at 0%   50%, rgba(200,70,20,0.22)  0%, transparent 60%),
                    radial-gradient(ellipse 60% 120% at 100% 50%, rgba(20,60,140,0.22)  0%, transparent 60%)
                  `,
                  mixBlendMode: 'multiply',
                  borderRadius: 0,
                }}
              />
            </div>
            <div>
              <h3 className="text-white text-3xl font-black uppercase mb-2 tracking-wide">Anna Schriver</h3>
              <p className="text-zinc-400 text-base uppercase tracking-widest">Filmmaker, Video Editor &amp; Creative Director</p>
            </div>
          </div>
          <h2 className="text-white leading-tight mb-10 text-center" style={{ fontSize: 'clamp(1.5rem, 4vw, 3.5rem)', fontWeight: 300 }}>
            Filmmaking and editing for brands, campaigns, and story-driven content that gets watched.
          </h2>
          <div className="flex justify-center">
            <a
              href="/contact"
              onClick={handleNavContact}
              className="inline-block text-white px-10 py-4 text-sm font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap rounded-md btn-glow"
              style={{ background: BTN_BLUE_GREY }}
            >
              START A PROJECT
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
